/**
 * Reçete (BOM) yönetimi — iki sekme:
 *  - Hammaddeler: Ingredient CRUD (ad, birim, son maliyet)
 *  - Reçeteler: ürün başına Bom + malzeme satırları (BomItem)
 *
 * Reçete oluşturma akışı: "Yeni Reçete" → ProductPickerModal'dan ürün seç
 * → boş bir Bom oluşturulur → kart açılır, malzemeler BomItemEditor ile
 * eklenir. Her ürüne 1 reçete (model `(tenant, product)` unique).
 */
import {
  Box, Card, Stack, Table, Badge, Loader, Center, Tabs, Button, Group,
  ActionIcon, Text, Tooltip, TextInput,
} from "@mantine/core";
import { IconPlus, IconEdit, IconTrash, IconInfoCircle, IconSearch } from "@tabler/icons-react";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useInfiniteResource, useRemove, useCreate } from "../../../shared/api";
import {
  PageHeader, EmptyState, MoneyDisplay, PriceHistoryDrawer, ResourceForm,
  confirmDelete, useInfiniteScrollRef, type FormField,
} from "../../../shared/ui";
import { ProductPickerModal } from "../components/ProductPickerModal";
import { BomItemEditor } from "../components/BomItemEditor";

const INGREDIENT_FIELDS: FormField[] = [
  { name: "name", label: "Ad", required: true },
  {
    name: "sku",
    label: "SKU",
    placeholder: "boş = otomatik (ör. KSP-0001)",
    // SKU kalemin kalıcı kimliği — oluşturmada atanır, sonra değiştirilemez.
    readOnlyOnEdit: true,
    readOnlyDescription: "🔒 Stok kodu kalıcıdır — oluşturulduktan sonra değiştirilemez",
  },
  { name: "unit", label: "Birim", required: true, placeholder: "KG / ADET / LT / GR" },
  { name: "description", label: "Açıklama", type: "textarea" },
  {
    name: "last_cost_minor",
    label: "Son Maliyet",
    type: "money",
    min: 0,
    // Yeni hammaddede başlangıç maliyeti girilir; sonrasında mal kabul
    // (irsaliye) onaylanınca AVCO ile otomatik güncellenir — elle düzenlenmez.
    description: "Başlangıç maliyeti — sonrası mal kabulden otomatik güncellenir",
    readOnlyOnEdit: true,
    readOnlyDescription: "🔒 Mal kabul (irsaliye) onaylanınca otomatik güncellenir — ağırlıklı ortalama (AVCO)",
  },
  { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
];

// Reçete düzenleme formu — ürün artık picker'dan geldiği için yalnızca
// notlar ve aktiflik düzenlenir (product değiştirilemez).
const BOM_FIELDS: FormField[] = [
  { name: "notes", label: "Notlar", type: "textarea" },
  { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
];

export function RecipePage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<string | null>("ingredients");

  // Sekme bazlı arama — her sekme kendi debounced metnini tutar.
  const [ingSearch, setIngSearch] = useState("");
  const [bomSearch, setBomSearch] = useState("");
  const [debouncedIng] = useDebouncedValue(ingSearch.trim(), 300);
  const [debouncedBom] = useDebouncedValue(bomSearch.trim(), 300);

  const ingQuery = useInfiniteResource<any>("ingredients", { search: debouncedIng });
  const bomQuery = useInfiniteResource<any>("boms", { search: debouncedBom });

  const ingRemove = useRemove("ingredients");
  const bomRemove = useRemove("boms");
  const bomCreate = useCreate("boms");

  const [ingOpen, ingHandlers] = useDisclosure(false);
  const [bomOpen, bomHandlers] = useDisclosure(false);
  const [pickerOpen, pickerHandlers] = useDisclosure(false);
  const [editIng, setEditIng] = useState<any | null>(null);
  const [editBom, setEditBom] = useState<any | null>(null);
  // "Son Maliyet" hücresine tıklanınca açılan son alış fiyatları drawer'ı.
  const [historyIng, setHistoryIng] = useState<any | null>(null);

  // Zaten reçetesi olan ürünler picker'da gizlenir (ürüne 1 BOM).
  const bomProductIds = bomQuery.items.map((b: any) => b.product);

  async function handleSelectProduct(product: { id: string; name: string }) {
    try {
      await bomCreate.mutateAsync({ product: product.id, is_active: true });
      qc.invalidateQueries({ queryKey: ["boms"] });
      notifications.show({ message: `${product.name} için reçete oluşturuldu`, color: "green" });
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail
          || e?.response?.data?.product?.[0]
          || "Reçete oluşturulamadı",
        color: "red",
      });
    }
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Reçete (BOM)"
        subtitle={`${ingQuery.total} hammadde, ${bomQuery.total} reçete`}
        actions={
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              if (tab === "ingredients") { setEditIng(null); ingHandlers.open(); }
              else pickerHandlers.open();
            }}
          >
            {tab === "ingredients" ? "Yeni Hammadde" : "Yeni Reçete"}
          </Button>
        }
      />
      <Tabs value={tab} onChange={setTab}>
        <Tabs.List>
          <Tabs.Tab value="ingredients">Hammaddeler ({ingQuery.total})</Tabs.Tab>
          <Tabs.Tab value="boms">Reçeteler ({bomQuery.total})</Tabs.Tab>
        </Tabs.List>

        {/* ---------- Hammaddeler ---------- */}
        <Tabs.Panel value="ingredients" pt="md">
          <Stack gap="sm">
            <TextInput
              placeholder="Hammadde adı veya SKU ile ara..."
              leftSection={<IconSearch size={16} />}
              value={ingSearch}
              onChange={(e) => setIngSearch(e.currentTarget.value)}
            />
            <Card padding="sm" radius="md" withBorder>
              <IngredientTable
                query={ingQuery}
                debouncedSearch={debouncedIng}
                onEdit={(i) => { setEditIng(i); ingHandlers.open(); }}
                onShowHistory={(i) => setHistoryIng(i)}
                onDelete={async (i) => {
                  if (!(await confirmDelete("Hammadde silinsin mi?"))) return;
                  try {
                    await ingRemove.mutateAsync(i.id);
                    notifications.show({ message: "Silindi", color: "green" });
                  } catch (e: any) {
                    notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
                  }
                }}
              />
            </Card>
          </Stack>
        </Tabs.Panel>

        {/* ---------- Reçeteler ---------- */}
        <Tabs.Panel value="boms" pt="md">
          <Stack gap="sm">
            <TextInput
              placeholder="Ürün adı veya SKU ile reçete ara..."
              leftSection={<IconSearch size={16} />}
              value={bomSearch}
              onChange={(e) => setBomSearch(e.currentTarget.value)}
            />
            <BomCardList
              query={bomQuery}
              debouncedSearch={debouncedBom}
              onEdit={(b) => { setEditBom(b); bomHandlers.open(); }}
              onDelete={async (b) => {
                if (!(await confirmDelete(`"${b.product_name}" reçetesi silinsin mi?`))) return;
                try {
                  await bomRemove.mutateAsync(b.id);
                  notifications.show({ message: "Reçete silindi", color: "green" });
                } catch (e: any) {
                  notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
                }
              }}
            />
          </Stack>
        </Tabs.Panel>
      </Tabs>

      <ResourceForm
        opened={ingOpen}
        onClose={ingHandlers.close}
        resource="ingredients"
        title={editIng ? "Hammadde Düzenle" : "Yeni Hammadde"}
        fields={INGREDIENT_FIELDS}
        editId={editIng?.id || null}
        initialValues={editIng || undefined}
      />
      <ResourceForm
        opened={bomOpen}
        onClose={bomHandlers.close}
        resource="boms"
        title={editBom ? `Reçete Düzenle — ${editBom?.product_name ?? ""}` : "Reçete Düzenle"}
        fields={BOM_FIELDS}
        editId={editBom?.id || null}
        initialValues={editBom || undefined}
      />

      <ProductPickerModal
        opened={pickerOpen}
        onClose={pickerHandlers.close}
        onSelect={handleSelectProduct}
        excludeIds={bomProductIds}
      />

      <PriceHistoryDrawer
        opened={!!historyIng}
        onClose={() => setHistoryIng(null)}
        title={historyIng?.name || "Hammadde"}
        endpoint={
          historyIng
            ? `/api/v1/ingredients/${historyIng.id}/price-history/`
            : null
        }
        unit={historyIng?.unit}
      />
    </Stack>
  );
}

/** useInfiniteResource dönüş tipi — alt component'lere geçilir. */
type InfiniteQuery<T> = {
  items: T[];
  total: number;
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
};

/** Hammadde tablosu — infinite scroll, son satırda sentinel. */
function IngredientTable({
  query, debouncedSearch, onEdit, onShowHistory, onDelete,
}: {
  query: InfiniteQuery<any>;
  debouncedSearch: string;
  onEdit: (i: any) => void;
  onShowHistory: (i: any) => void;
  onDelete: (i: any) => void;
}) {
  const sentinelRef = useInfiniteScrollRef<HTMLTableRowElement>({
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    itemCount: query.items.length,
  });

  if (query.isLoading) return <Center py="xl"><Loader /></Center>;
  if (query.items.length === 0) {
    return (
      <Center py="xl">
        <EmptyState title={debouncedSearch ? "Eşleşen hammadde yok" : "Hammadde yok"} />
      </Center>
    );
  }

  return (
    <Table.ScrollContainer minWidth={560}>
    <Table highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Ad</Table.Th>
          <Table.Th>SKU</Table.Th>
          <Table.Th>Birim</Table.Th>
          <Table.Th ta="right">
            <Group gap={4} justify="flex-end" wrap="nowrap">
              <span>Son Maliyet</span>
              <Tooltip
                label="Mal kabul (irsaliye) onaylandıkça ağırlıklı ortalama (AVCO) ile otomatik güncellenir"
                multiline
                w={240}
                withinPortal
              >
                <IconInfoCircle size={14} color="var(--mantine-color-gray-5)" />
              </Tooltip>
            </Group>
          </Table.Th>
          <Table.Th>Durum</Table.Th>
          <Table.Th></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {query.items.map((i: any) => (
          <Table.Tr key={i.id}>
            <Table.Td fw={500}>{i.name}</Table.Td>
            <Table.Td><Badge variant="light">{i.sku || "-"}</Badge></Table.Td>
            <Table.Td>{i.unit}</Table.Td>
            <Table.Td ta="right">
              {/* Maliyet hammaddenin ölçü birimi başınadır. Maliyetli kalemlerde
                  tıklanınca son alış fiyatları (mal kabul geçmişi) drawer'ı açılır. */}
              {i.last_cost_minor > 0 ? (
                <Tooltip label="Son alış fiyatlarını gör" withinPortal>
                  <Group
                    gap={4}
                    justify="flex-end"
                    wrap="nowrap"
                    style={{ cursor: "pointer" }}
                    onClick={() => onShowHistory(i)}
                  >
                    <MoneyDisplay minor={i.last_cost_minor} />
                    <Text span size="xs" c="dimmed"> / {i.unit}</Text>
                  </Group>
                </Tooltip>
              ) : (
                <>
                  <MoneyDisplay minor={i.last_cost_minor} />
                  <Text span size="xs" c="dimmed"> / {i.unit}</Text>
                </>
              )}
            </Table.Td>
            <Table.Td>
              {i.is_active
                ? <Badge color="green">Aktif</Badge>
                : <Badge color="gray">Pasif</Badge>}
            </Table.Td>
            <Table.Td>
              <Group gap={4}>
                <ActionIcon variant="subtle" onClick={() => onEdit(i)} aria-label="Düzenle">
                  <IconEdit size={16} />
                </ActionIcon>
                <ActionIcon variant="subtle" color="red" onClick={() => onDelete(i)} aria-label="Sil">
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Table.Td>
          </Table.Tr>
        ))}
        {query.hasNextPage && (
          <Table.Tr ref={sentinelRef}>
            <Table.Td colSpan={6}>
              <Center py="sm"><Loader size="xs" /></Center>
            </Table.Td>
          </Table.Tr>
        )}
      </Table.Tbody>
    </Table>
    </Table.ScrollContainer>
  );
}

/** Reçete kart listesi — infinite scroll, son kartta sentinel. */
function BomCardList({
  query, debouncedSearch, onEdit, onDelete,
}: {
  query: InfiniteQuery<any>;
  debouncedSearch: string;
  onEdit: (b: any) => void;
  onDelete: (b: any) => void;
}) {
  const sentinelRef = useInfiniteScrollRef<HTMLDivElement>({
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    itemCount: query.items.length,
  });

  if (query.isLoading) return <Center py="xl"><Loader /></Center>;
  if (query.items.length === 0) {
    return (
      <EmptyState
        title={debouncedSearch ? "Eşleşen reçete yok" : "Reçete yok"}
        {...(debouncedSearch ? {} : { description: "Bir ürün için reçete oluşturun, sipariş onayında stok otomatik düşsün." })}
      />
    );
  }

  return (
    <Stack gap="md">
      {query.items.map((b: any) => (
        <Card key={b.id} padding="md" radius="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Stack gap={0}>
              <Group gap="xs">
                <strong>{b.product_name}</strong>
                {b.is_active
                  ? <Badge color="green" size="sm">Aktif</Badge>
                  : <Badge color="gray" size="sm">Pasif</Badge>}
              </Group>
              {b.notes && <Text size="xs" c="dimmed">{b.notes}</Text>}
            </Stack>
            <Group gap={4}>
              <ActionIcon variant="subtle" onClick={() => onEdit(b)} aria-label="Reçeteyi düzenle">
                <IconEdit size={16} />
              </ActionIcon>
              <ActionIcon variant="subtle" color="red" onClick={() => onDelete(b)} aria-label="Reçeteyi sil">
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          </Group>
          <BomItemEditor bomId={b.id} items={b.items || []} />
        </Card>
      ))}
      {query.hasNextPage && (
        <Box ref={sentinelRef}>
          <Center py="sm"><Loader size="sm" /></Center>
        </Box>
      )}
    </Stack>
  );
}
